import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LoginPage from "./login/LoginPage";
import LoginCallbackPage from "./login/LoginCallbackPage";
import LogoutCallbackPage from "./login/LogoutCallbackPage";
import MainPage from "./main/MainPage";
import FatalErrorPage from "./error/FatalErrorPage";
import { AppConfig, Locale, AnyType } from "./common/constants";
import locales_ja from "./locales/ja.json";
import locales_en from "./locales/en.json";
import AuthService from "./common/AuthService";
import Loading from "./common/Loading";
import { LicenseInfo } from "@mui/x-data-grid-pro";
import { getStorageItem, setStorageItem } from "./common/StorageItem";

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: locales_en },
    ja: { translation: locales_ja },
  },
  lng: Locale.DEFAULT,
  fallbackLng: Locale.NOTEXIST,
});

LicenseInfo.setLicenseKey(
  "108c4dd74638ccb771995e41022ed93bT1JERVI6MzcwMTAsRVhQSVJZPTE2NzUzMTY0NzMwMDAsS0VZVkVSU0lPTj0x"
);

type AppProps = AnyType;

interface IState {
  /**
   * ログインユーザー名
   */
  name: string;
  /**
   * 致命的なエラーの画面を表示する場合true
   */
  fatalError: boolean;
  /**
   * 致命的なエラーのhttpステータスコード
   */
  status: number | undefined;
}

class App extends React.Component<AppProps, IState> {
  private authService: AuthService | undefined;

  constructor(props: AppProps) {
    super(props);

    this.state = { name: "", fatalError: false, status: undefined };

    document.title = i18n.t("header_product_name");

    if (process.env.REACT_APP_CONSOLE_LOG_ENABLED !== "true") {
      // eslint-disable-next-line
      console.log = function () {};
    }

    if (process.env.REACT_APP_CONSOLE_TIME_ENABLED !== "true") {
      // eslint-disable-next-line
      console.time = function () {};
      // eslint-disable-next-line
      console.timeEnd = function () {};
    }

    const storage = getStorageItem();
    if (storage) {
      i18n.changeLanguage(storage.language);
    } else {
      setStorageItem({ language: i18n.language });
    }

    this.handleLogout = this.handleLogout.bind(this);
    this.setAuthUser = this.setAuthUser.bind(this);
    this.handleFatalError = this.handleFatalError.bind(this);
  }

  handleLogout(): void {
    const service = this.authService;
    if (service) {
      service.logout();
    }
  }

  setAuthUser(name: string, callback?: () => void): void {
    if (this.state.name !== name) {
      this.setState({ name: name });
    }
    if (callback) {
      callback();
    }
  }

  handleFatalError(status: number | undefined) {
    this.setState({ fatalError: true, status: status });
  }

  getApp(): JSX.Element {
    switch (AppConfig.MODE) {
      case AppConfig.MODE_OFFLINE:
        return <MainPage onFatalError={this.handleFatalError} />;
      case AppConfig.MODE_CLOUD:
        this.authService = new AuthService();
        return (
          <BrowserRouter>
            <Routes>
              <Route
                path="/callback"
                element={
                  <LoginCallbackPage
                    authService={this.authService}
                    setAuthUser={this.setAuthUser}
                    onFatalError={this.handleFatalError}
                  />
                }
              />
              <Route path="/scallback" element={<Loading />} />
              <Route
                path="/home"
                element={
                  <MainPage
                    authService={this.authService}
                    name={this.state.name}
                    onLogout={this.handleLogout}
                    onFatalError={this.handleFatalError}
                  />
                }
              />
              <Route
                path="/"
                element={
                  <LoginPage
                    authService={this.authService}
                    setAuthUser={this.setAuthUser}
                    onFatalError={this.handleFatalError}
                  />
                }
              />
              <Route path="/logout" element={<LogoutCallbackPage />} />
              <Route
                path="*"
                element={
                  <FatalErrorPage status={404} onLogout={this.handleLogout} />
                }
              />
            </Routes>
          </BrowserRouter>
        );
      default:
        return <FatalErrorPage status={404} onLogout={this.handleLogout} />;
    }
  }

  render(): JSX.Element {
    const app = this.state.fatalError ? (
      <FatalErrorPage status={this.state.status} onLogout={this.handleLogout} />
    ) : (
      this.getApp()
    );
    return app;
  }
}

/**
 * @class App
 */
export default App;
